import React, { useState, useEffect } from "react";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";

// MUI Components
import {
  Pagination,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { ToggleOn, ToggleOff } from "@mui/icons-material";

// API & Utils
import BuildingApi from "../../Classes/API/BuildingApi";
import toast from "react-hot-toast";

const BuildingList = ({ refreshKey }) => {
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // Core fetch function structured identically to your dynamic object payload preferences
  const fetchBuildings = async (
    currentSearch = searchTerm,
    currentInactive = showInactive,
    currentPage = pageNumber,
  ) => {
    setLoading(true);
    try {
      const req = {
        name: currentSearch,
        showInactive: currentInactive,
        pagingParams: {
          pageNumber: currentPage,
          pageSize: 5,
        },
      };

      const res = await BuildingApi.GetBuildings(req);

      if (res.status === "Success" && res.data) {
        setBuildings(res.data.list || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching buildings:", error);
      toast.error("Failed to load buildings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuildings(searchTerm, showInactive, pageNumber);
  }, [refreshKey, showInactive, pageNumber]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBuildings(searchTerm, showInactive, pageNumber);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const handleToggle = async (row) => {
    try {
      const res = await BuildingApi.UpdateBuilding(row.id, {
        ...row,
        isActive: !row.isActive,
      });
      if (res.status === "Success") {
        toast.success("Updated successfully");
        fetchBuildings(searchTerm, showInactive, pageNumber);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error("Network error");
    }
  };

  const updateCapacity = async (row, newCap) => {
    const val = parseInt(newCap);
    if (val === row.capacity || isNaN(val)) return;

    try {
      const res = await BuildingApi.UpdateBuilding(row.id, {
        ...row,
        capacity: val,
      });
      if (res.status === "Success") {
        toast.success("Capacity saved");
        fetchBuildings(searchTerm, showInactive, pageNumber);
      }
    } catch (err) {
      toast.error("Error updating capacity");
    }
  };

  const columns = [
    {
      Header: "Building Name",
      accessor: "name",
      Cell: ({ value, row }) => (
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ opacity: row.original.isActive ? 1 : 0.5 }}
        >
          {value || "Unnamed Building"}
        </MDTypography>
      ),
    },
    {
      Header: "Capacity",
      accessor: "capacity",
      align: "center",
      Cell: ({ value, row }) => (
        <MDInput
          type="number"
          size="small"
          defaultValue={value}
          label="Capacity"
          onBlur={(e) => updateCapacity(row.original, e.target.value)}
          sx={{ width: "100px" }}
          disabled={!row.original.isActive}
        />
      ),
    },
    {
      Header: "Status",
      accessor: "isActive",
      Cell: ({ value }) => (
        <MDBadge
          variant="gradient"
          color={value ? "success" : "secondary"}
          size="xs"
          badgeContent={value ? "Active" : "Inactive"}
          container
        />
      ),
    },
    {
      Header: "Toggle",
      Cell: ({ row }) => (
        <IconButton
          onClick={() => handleToggle(row.original)}
          color={row.original.isActive ? "info" : "error"}
        >
          {row.original.isActive ? (
            <ToggleOn fontSize="large" />
          ) : (
            <ToggleOff fontSize="large" />
          )}
        </IconButton>
      ),
    },
  ];

  return (
    <MDBox>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        mb={1}
        gap={2}
      >
        <MDBox flexGrow={1}>
          <MDInput
            fullWidth
            label="Search by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageNumber(1);
            }}
          />
        </MDBox>

        <FormControlLabel
          sx={{ whiteSpace: "nowrap", ml: 2 }}
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => {
                setShowInactive(e.target.checked);
                setPageNumber(1);
              }}
              color="info"
            />
          }
          label={
            <MDTypography variant="button" color="text">
              Show Deactivated
            </MDTypography>
          }
        />
      </MDBox>
      {/* Main Table */}
      <MDBox position="relative">
        <DataTable
          table={{ columns, rows: buildings }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={true}
          noEndBorder
        />

        {loading && (
          <MDBox
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor="rgba(255,255,255,0.6)"
            zIndex={2}
          >
            <CircularProgress color="info" />
          </MDBox>
        )}
      </MDBox>

      {/* Modern Pagination Footer */}
      {totalPages > 1 && (
        <MDBox display="flex" justifyContent="center" mt={3} pb={2}>
          <Pagination
            count={totalPages}
            page={pageNumber}
            onChange={(e, value) => setPageNumber(value)}
            color="info"
            variant="outlined"
            shape="rounded"
          />
        </MDBox>
      )}
    </MDBox>
  );
};

export default BuildingList;
